import { __rest } from "tslib";
import * as React from 'react';
import { classes, st, vars } from './WowImage.st.css';
import { Error } from '@wix/wix-ui-icons-common/on-stage';
import { Image as WixImage, initCustomElement } from '@wix/image';
import { calculateDimensions, resolveFocalPointCoordinates, } from '../Image/ImageUtils';
import { Text } from '../Text';
import { Spinner, SpinnerTypes } from '../Spinner';
import { ImageResizeOptions, ImageAspectRatioPresets, ImageLoadingBehaviorOptions, ImageHoverEffectOptions, } from '../Image/types';
import { extractWixMediaId } from './utils';
const isTestEnv = process.env.NODE_ENV === 'test';
if (isTestEnv && typeof window !== 'undefined' && !window.ResizeObserver) {
    window.ResizeObserver = class Observer {
        /* eslint-disable-next-line */
        constructor() { }
        disconnect() { }
        observe() { }
        unobserve() { }
    };
}
/** Image is a component to literally display an image - whether an absolute with full URL or a media platform item with relative URI */
class WowImage extends React.Component {
    constructor(props) {
        super(props);
        this.state = { isLoaded: false, boundingRectDimensions: null, isError: false };
        this.containerRef = React.createRef();
        this.imageRef = React.createRef();
        this._onLoad = (event) => {
            const { onLoad } = this.props;
            this.setState({ isLoaded: true, isError: false });
            onLoad && onLoad(event);
        };
        this._onError = (event) => {
            const { onError } = this.props;
            if (!this.state.isError) {
                this.setState({ isError: true });
            }
            onError && onError(event);
        };
        // TODO: Remove this guard after it's migrated to wix/image
        if (typeof window !== 'undefined') {
            initCustomElement();
        }
    }
    componentDidMount() {
        var _a;
        const { width, height, aspectRatio } = this.props;
        const { current: imageElement } = this.imageRef;
        // Updating the state in case it's rendered with SSR and the image is loaded on the client even before registering the `onLoad` event
        if (!this.state.isLoaded && (imageElement === null || imageElement === void 0 ? void 0 : imageElement.complete)) {
            this.setState({ isLoaded: true });
        }
        // Updating the state only if we don't have enough information to calculate the dimensions
        if (!(width && height) && !((width || height) && aspectRatio)) {
            const { width: boundingRectWidth, height: boundingRectHeight } = (_a = this.containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            this.setState({
                boundingRectDimensions: {
                    width: boundingRectWidth,
                    height: boundingRectHeight,
                },
            });
        }
    }
    render() {
        const _a = this.props, { src, alt, fluid, width, height, resize, onLoad, onError, className, focalPoint, aspectRatio, hoverEffect, sourceWidth, sourceHeight, errorMessage, loadingBehavior, stretchImage, shouldUseLQIP, isInFirstFold, isSEOBot, forceImageContain, containerAspectRatio } = _a, imageProps = __rest(_a, ["src", "alt", "fluid", "width", "height", "resize", "onLoad", "onError", "className", "focalPoint", "aspectRatio", "hoverEffect", "sourceWidth", "sourceHeight", "errorMessage", "loadingBehavior", "stretchImage", "shouldUseLQIP", "isInFirstFold", "isSEOBot", "forceImageContain", "containerAspectRatio"]);
        const { isLoaded, boundingRectDimensions, isError } = this.state;
        // If full wixstatic url is passed wix media id is extracted to use with wow image
        const imageSrc = extractWixMediaId(src) || src;
        const isAbsoluteUrl = imageSrc && imageSrc.match('^(data:image|(blob:)?https?://)');
        const isBlurLoading = loadingBehavior === ImageLoadingBehaviorOptions.blur;
        const hasLoadingEffect = loadingBehavior && loadingBehavior !== ImageLoadingBehaviorOptions.none;
        const zoomOnHover = hoverEffect === ImageHoverEffectOptions.zoom;
        const aspectRatioAsNumber = ImageAspectRatioPresets[aspectRatio] || aspectRatio;
        const focalPointCoordinates = focalPoint && resolveFocalPointCoordinates(focalPoint);
        // Taking the dimensions from props or from its bounding rectangle in case they're missing
        const targetDimensions = {
            width: width || (boundingRectDimensions === null || boundingRectDimensions === void 0 ? void 0 : boundingRectDimensions.width),
            height: height || (boundingRectDimensions === null || boundingRectDimensions === void 0 ? void 0 : boundingRectDimensions.height),
        };
        // Calculating the dimensions considering the given values and aspect ratio
        const calculatedDimensions = calculateDimensions(Object.assign(Object.assign({}, targetDimensions), { aspectRatio: aspectRatioAsNumber }));
        const calculatedDimensionsWithHover = calculatedDimensions
            ? {
                width: (calculatedDimensions === null || calculatedDimensions === void 0 ? void 0 : calculatedDimensions.width) * (zoomOnHover ? 1.07 : 1),
                height: (calculatedDimensions === null || calculatedDimensions === void 0 ? void 0 : calculatedDimensions.height) * (zoomOnHover ? 1.07 : 1),
            }
            : null;
        const shouldUseOldImageDims = resize === ImageResizeOptions.contain && fluid;
        const calculatedContainerAspectRatio = (calculatedDimensions === null || calculatedDimensions === void 0 ? void 0 : calculatedDimensions.width) / (calculatedDimensions === null || calculatedDimensions === void 0 ? void 0 : calculatedDimensions.height);
        return (React.createElement("div", { ref: this.containerRef, "data-aspect-ratio": aspectRatio, "data-source-width": sourceWidth, "data-source-height": sourceHeight, "data-resize": resize, "data-use-lqip": shouldUseLQIP, "data-first-fold": isInFirstFold, "data-is-seo-bot": isSEOBot, className: st(classes.root, {
                resize,
                focalPoint: isAbsoluteUrl && Boolean(focalPointCoordinates),
                fluid,
                hoverEffect,
                loadSpinner: loadingBehavior === ImageLoadingBehaviorOptions.spinner,
                loaded: hasLoadingEffect && isLoaded,
                isError,
                noImage: !imageSrc,
                stretchImage,
                forceImageContain,
                horizontalContainer: (containerAspectRatio !== null && containerAspectRatio !== void 0 ? containerAspectRatio : calculatedContainerAspectRatio) > 1,
                verticalContainer: (containerAspectRatio !== null && containerAspectRatio !== void 0 ? containerAspectRatio : calculatedContainerAspectRatio) <= 1,
            }, className), style: Object.assign(Object.assign(Object.assign({}, (!fluid && !forceImageContain && calculatedDimensions)), (isAbsoluteUrl &&
                focalPointCoordinates && {
                [vars.focalPointX]: `${focalPointCoordinates.x}%`,
                [vars.focalPointY]: `${focalPointCoordinates.y}%`,
            })), (!stretchImage &&
                Boolean(sourceWidth) &&
                Boolean(sourceHeight) && {
                '--wut-source-width': `${sourceWidth}px`,
                '--wut-source-height': `${sourceHeight}px`,
            })), "data-hook": this.props['data-hook'] },
            src ? (React.createElement(WixImage, Object.assign({}, imageProps, { uri: imageSrc, name: imageSrc, width: sourceWidth || (shouldUseOldImageDims && width) || undefined, height: sourceHeight || (shouldUseOldImageDims && height) || undefined, displayMode: resize === 'cover' ? 'fill' : 'fit', alt: alt, focalPoint: focalPointCoordinates, containerWidth: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.width, containerHeight: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.height }, (zoomOnHover && {
                targetWidth: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.width,
                targetHeight: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.height,
                skipMeasure: true,
            }), { className: classes.image, onError: this._onError, onLoad: this._onLoad, placeholderTransition: isBlurLoading ? 'blur' : null, shouldUseLQIP: shouldUseLQIP ? true : isBlurLoading, isInFirstFold: isInFirstFold, isSEOBot: isSEOBot }))) : null,
            isError && (React.createElement("div", { className: classes.errorWrapper },
                React.createElement(Error, { className: classes.errorIcon }),
                React.createElement(Text, { className: classes.errorMessage },
                    React.createElement("span", { className: classes.srError }, alt),
                    errorMessage))),
            loadingBehavior === ImageLoadingBehaviorOptions.spinner ? (React.createElement("div", { className: classes.spinnerOverlay, "aria-hidden": "true" },
                React.createElement(Spinner, { isCentered: true, diameter: 24, type: SpinnerTypes.regular, className: classes.spinner }))) : null));
    }
}
WowImage.displayName = 'Image';
WowImage.defaultProps = {
    resize: ImageResizeOptions.contain,
    errorMessage: 'Image not found',
    stretchImage: true,
    forceImageContain: false,
};
export { WowImage };
//# sourceMappingURL=WowImage.js.map